import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { FacilityInformation, FacilityFarmCapacityInformation, FacilityProcessDetails } from "../../../sc-models/facility-product-dto";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { FARM_CODES, SUPPLY_CHAIN_CODES, ALL_FARM_CATEGORIES, FarmCategoryUtils } from "../../../constants/farm-categories";
import useNotification from "../../../../../../shared/hooks/useNotification";

interface FarmCapacityInfoProps {
  formRef:any,
  facilityData: FacilityInformation;
  facilityFarmCapacityInfo: FacilityFarmCapacityInformation[]; // Expecting an array
  facilityStandard: string[]; // Added for conditional validation
  facilityProcessInfo: FacilityProcessDetails[]; // Added for validation
  setScSubsequentFacilityButtonFlag: React.Dispatch<React.SetStateAction<boolean>>; // Added for button control
  setFacilityFarmCapacityInfo: React.Dispatch<
    React.SetStateAction<FacilityFarmCapacityInformation[]>
  >;
}

const FacilityFarmCapacityInfo: React.FC<FarmCapacityInfoProps> = ({
  formRef,
  facilityData,
  facilityFarmCapacityInfo,
  facilityStandard,
  facilityProcessInfo,
  setScSubsequentFacilityButtonFlag,
  setFacilityFarmCapacityInfo
}) => {
  const { updateField } = useFormActions("sc-facilitydetails");
  const [data, setData] = useState<FacilityFarmCapacityInformation[]>(
    facilityFarmCapacityInfo
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<FacilityFarmCapacityInformation[]>(
    facilityFarmCapacityInfo
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterialCode,setRawMaterialCode] = useState<any[]>([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave, setFacilityProcessCategoryCodes}=useTransactionCertificateStore()

  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const { notify } = useNotification();


  // Function to compute if farm number is required (from facility-information.tsx)
  const computeFarmNumRequired = (codes: string[]): boolean => {
    const farmCodes = FarmCategoryUtils.getFarmCodesSet();
    const supplyChainCodes = FarmCategoryUtils.getSupplyChainCodesSet();

    const codeSet = new Set(codes);
    const hasFarmCode = codes.some((code) => farmCodes.has(code));

    if (!hasFarmCode) return false;

    const nonFarmCodes = codes.filter((code) => !farmCodes.has(code));

    // Case 1: Only farm codes
    if (nonFarmCodes.length === 0) return true;

    // Case 2: Farm + supply chain codes only
    const allNonFarmAreSupplyChain = nonFarmCodes.every((code) =>
      supplyChainCodes.has(code)
    );
    if (allNonFarmAreSupplyChain) return true;

    // Case 3: Farm + other codes (excluding supply chain)
    const hasOtherNonSupplyChain = nonFarmCodes.some(
      (code) => !supplyChainCodes.has(code)
    );
    if (hasOtherNonSupplyChain) return true;

    return false;
  };

  // Check if farm number is required based on facility standards and facility type
  const facilityType = facilityData.facilityType;
  const requiresFarmCapacity = facilityType === "MF" || facilityType === "FA";
  const isFarmNumRequired = requiresFarmCapacity && computeFarmNumRequired(facilityStandard);

  // Farm categories that require farm animals data
  const farmCategories = ALL_FARM_CATEGORIES;

  const validateFarmRequirements = () => {
    // The previous logic incorrectly disabled the Save button if there were no animal counts,
    // which broke plant-based farms (like Cotton).
    // Let the validateBeforePost function in index.tsx handle form-level validation and show
    // helpful error toasts to the user if data is missing, rather than silently disabling the button.
    setScSubsequentFacilityButtonFlag(true);
    return true;
  };

  useEffect(() => {
      setRestrictPopUpSave(false);
      // Initialize validation on component mount
      validateFarmRequirements();
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, [])

  // Run validation whenever relevant data changes
  useEffect(() => {
    validateFarmRequirements();
  }, [data, facilityProcessInfo]); // Use local data state instead of prop

  const validationSchema = Yup.object().shape({
    facilityFarmRawMaterialCode: Yup.string().required("Please enter farm raw material code"),
    facilityFarmRawMaterialCapacity: Yup.string().when("facilityFarmRawMaterialCode", {
      is: (val: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'].includes(val),
      then: (schema) => schema.required("Facility Farm Raw Material Capacity is required"),
      otherwise: (schema) => schema.nullable(),
    }).matches(/^\d+(\.\d+)?$/, { message: "Must be a valid number", excludeEmptyString: true }),
    facilityFarmRawMaterialNumberOfAnimals: Yup.string().when("facilityFarmRawMaterialCode", {
      is: (val: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007'].includes(val),
      then: (schema) => schema.required("Facility Farm Raw Material Number Of Animals is required"),
      otherwise: (schema) => schema.nullable(),
    }).matches(/^\d+$/, { message: "Must be an integer", excludeEmptyString: true }),
  });

  useEffect(() => {
    const getRawMaterialCodes = async () => {
        try {
          const result: any = await dtrackItServiceV4.getRawMaterialCodes()
          //(`materiaCodes result ${JSON.stringify(result)}`)
  
          const materiaCodes = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
          //(`materiaCodes ${materiaCodes}`)
          setRawMaterialCode(materiaCodes);
        } catch (err) {
          //(`Error in Fetching Organic Farm Standard Code ${err}`)
        }
      };

    getRawMaterialCodes();
  }, [])
  useEffect(() => {
    updateField("facilityProcessDetails", data)
  }, [data])

  const formik = useFormik<FacilityFarmCapacityInformation>({
    initialValues: {
      facilityFarmRawMaterialCode: "",
      facilityFarmRawMaterialCapacity: "",
      facilityFarmRawMaterialCodeDescription:"",
      facilityFarmRawMaterialNumberOfAnimals: ""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: FacilityFarmCapacityInformation = {
      facilityFarmRawMaterialCode: "",
      facilityFarmRawMaterialCapacity: "",
      facilityFarmRawMaterialCodeDescription:"",
      facilityFarmRawMaterialNumberOfAnimals: ""
    };
    setRestrictPopUpSave(true);
    const newData = [...data,newRow];
    setData(newData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);

    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = {...formik.values,
      facilityFarmRawMaterialCodeDescription:rawMaterialCode?.find((rm:any)=>rm?.value===formik?.values?.facilityFarmRawMaterialCode)?.label
    };
    const currentRowData = data[editingRowIndex];
    const mandatoryCapacityCodes = ['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'];
    if(!currentRowData?.facilityFarmRawMaterialCapacity?.trim() && mandatoryCapacityCodes.includes(currentRowData.facilityFarmRawMaterialCode)){
      notify(`Facility Farm Raw Material Capacity is required for ${formik?.values?.facilityFarmRawMaterialCode}`, 'error');
      return;
    }

    const mandatoryAnimalsCodes = ['RM0083', 'RM0330', 'RM0064', 'RM0007'];
    if((!currentRowData?.facilityFarmRawMaterialNumberOfAnimals || !currentRowData?.facilityFarmRawMaterialNumberOfAnimals.trim()) && mandatoryAnimalsCodes.includes(currentRowData.facilityFarmRawMaterialCode)){
      notify(`Facility Farm Raw Material Number of Animals is required for ${formik?.values?.facilityFarmRawMaterialCode}`, 'error');
      return;
    }

    if (currentRowData?.facilityFarmRawMaterialCapacity?.trim() && !/^\d+(\.\d+)?$/.test(currentRowData.facilityFarmRawMaterialCapacity.trim())) {
      notify("Facility Farm Raw Material Capacity must be a valid number", "error");
      return;
    }

    if (currentRowData?.facilityFarmRawMaterialNumberOfAnimals?.trim() && !/^\d+$/.test(currentRowData.facilityFarmRawMaterialNumberOfAnimals.trim())) {
      notify("Facility Farm Raw Material Number Of Animals must be an integer", "error");
      return;
    }
    // setSubmissionFlag(updatedRow)
    // const message = getValidationMessage(updatedRow);
    // setShowWarning(!!message);
    // setWarningMessage(message);

    const newData = data?.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setFacilityFarmCapacityInfo(newData); // Update parent with current data
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  // Helper function to generate helper text for farm animals field
  const getFarmAnimalsHelperText = () => {
    const selectedStandards = facilityStandard || [];
    const rafStandards = selectedStandards.filter(
      (standard: string) => ["RMS", "RAS", "RWS"].includes(standard)
    );
    const hasRDS = selectedStandards.includes("RDS");

    if (rafStandards.length > 0 || hasRDS) {
      let message = "Submit ";

      if (rafStandards.length > 0) {
        message += `number of animals for ${rafStandards.join(", ")}`;
      }

      if (rafStandards.length > 0 && hasRDS) {
        message += " and ";
      }

      if (hasRDS) {
        message += "number of birds for RDS";
      }

      return message;
    }
    return "";
  };

  const columns: EditableColumnDef<FacilityFarmCapacityInformation>[] = [
    {
      accessorKey: "facilityFarmRawMaterialCode",
      header: "Facility Farm Raw Material Code",
      Header: <span> {requiresFarmCapacity && <i style={{ color: 'red' }}>* </i>}Facility Farm Raw Material Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="facilityFarmRawMaterialCode"
          value={value}
          placeholder="Enter value"
          options={rawMaterialCode}
          required = {requiresFarmCapacity}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "facilityFarmRawMaterialCapacity",
      header: "Facility Farm Raw Material Capacity",
      Header: <span> {(data.some((row: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'].includes(row?.facilityFarmRawMaterialCode))) && <i style={{ color: 'red' }}>* </i>}Facility Farm Raw Material Capacity</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="facilityFarmRawMaterialCapacity"
          required={['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'].includes(value?.facilityFarmRawMaterialCode || data[editingRowIndex]?.facilityFarmRawMaterialCode)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />

      ),
    },
    {
      accessorKey: "facilityFarmRawMaterialNumberOfAnimals",
      header: "Facility Farm Raw Material Number Of Animals",
      Header: <span> {(data.some((row: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007'].includes(row?.facilityFarmRawMaterialCode))) && <i style={{ color: 'red' }}>* </i>}Facility Farm Raw Material Number Of Animals</span>,
      renderEditCell: ({ value, onChange }) => (
        <Box>
          <CustomTextField
            name="facilityFarmRawMaterialNumberOfAnimals"
            required={['RM0083', 'RM0330', 'RM0064', 'RM0007'].includes(value?.facilityFarmRawMaterialCode || data[editingRowIndex]?.facilityFarmRawMaterialCode)}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter Number of Animals"
            sx={{ mb: 2 }}
          />
          {/* {getFarmAnimalsHelperText() && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {getFarmAnimalsHelperText()}
            </Typography>
          )} */}
        </Box>
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setFacilityFarmCapacityInfo(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={!formik.isValid || isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Facility Farm Capacity Information
          </StyledButton>
        </Box>
        {showWarning && (
          <Box sx={{ p: 2, mb: 2, bgcolor: '#FFF3E0', borderRadius: 1, border: '1px solid #FFB74D' }}>
            <Typography color="warning.dark">
              {warningMessage}
            </Typography>
          </Box>
        )}
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default FacilityFarmCapacityInfo;
